/**
 * État dérivé d'un plan d'adhésion :
 *  - isPlanActive / isPlanFull / isSeasonEnded
 *  - remainingPlaces / durationLabel
 * Sort du composant /a/[slug]/index.vue pour réduire la taille.
 */
import type { ComputedRef } from "vue";
import { formatInParis } from "@donoor/utils/dates";
import { saisonDuTarifTerminee } from "~/utils/tarifAdhesion";
import { fuseauDe } from "~/utils/heureAssociation";
import type { MembershipPlanPublic } from "~/types/membership";
import { LOCALE_INTL, type Langue } from "~/utils/langue";

export function useMembershipPlanStatus(
  plan: Ref<MembershipPlanPublic | null>,
  /**
   * Mode APERÇU du dashboard (`?preview=true`). Un plan encore en BROUILLON y
   * compte comme actif : sinon l'aperçu affiche « Adhésion indisponible » à la
   * place du tunnel, c'est-à-dire exactement ce que l'organisation vient
   * vérifier avant de publier (Karim 2026-08-30). Parité page événement, qui
   * affiche son tunnel en aperçu et se contente d'un bandeau d'avertissement.
   *
   * ⚠️ Purement visuel : l'inscription reste refusée côté serveur, où
   * `createOrder` relit le plan SANS le drapeau d'aperçu — un plan en
   * brouillon y est donc introuvable (404).
   */
  isPreview?: Ref<boolean> | ComputedRef<boolean>,
) {
  const { t, locale } = useI18n();
  const isPlanActive = computed(
    () =>
      plan.value?.status === "active" ||
      (isPreview?.value === true && plan.value?.status === "draft"),
  );

  // Plan clôturé (status 'closed') : inscriptions fermées, page consultable.
  // Le backend renvoie aussi le statut 'closed' pour le GROUPE quand TOUS les
  // tarifs sont clôturés. Karim 2026-06-27.
  const isPlanClosed = computed(() => plan.value?.status === "closed");

  const isPlanFull = computed(() => {
    if (!plan.value?.max_members) return false;
    return (plan.value.member_count || 0) >= plan.value.max_members;
  });

  const remainingPlaces = computed(() => {
    if (!plan.value?.max_members) return null;
    return Math.max(0, plan.value.max_members - (plan.value.member_count || 0));
  });

  // Saison terminée : minuit à la FIN du dernier jour, à l'heure de
  // l'association — même règle que l'API (avant : minuit au DÉBUT du dernier
  // jour, la saison se fermait un jour trop tôt). Karim 2026-10-09.
  const isSeasonEnded = computed(() => saisonDuTarifTerminee(plan.value));

  const durationLabel = computed(() => {
    if (!plan.value) return "";
    if (plan.value.duration_type === "season") {
      if (plan.value.season_end_date) {
        // `season_end_date` ("YYYY-MM-DD") est parsée minuit UTC ; affichée en
        // heure de Paris (fuseau métier) pour ne pas reculer d'un jour sur un
        // navigateur à l'ouest de UTC. Source unique = `@donoor/utils/dates`.
        // ⚠️ La chaîne est passée TELLE QUELLE à `formatInParis` : l'envelopper
        // dans `new Date()` annulerait le correctif, puisque `new Date()` aurait
        // déjà lu une chaîne sans fuseau comme une heure locale.
        //
        // `membership_plans.season_end_date` est un `timestamp WITHOUT time
        // zone` et vaut « 2027-08-30 22:00:00 », c'est-à-dire MINUIT heure de
        // Paris. Mal lue, cette valeur affichait « 30 août » au lieu du
        // 31 août : la saison semblait finir un jour trop tôt. Constaté le
        // 2026-09-19.
        //
        // Lot 2 (2026-09-25) : jour lu dans le fuseau de l'ASSOCIATION
        // (`plan.timezone`, Paris par défaut) — minuit en Martinique reste le
        // bon jour pour tout le monde.
        const localeIntl = LOCALE_INTL[locale.value as Langue] ?? LOCALE_INTL.fr;
        return t("adhesion.useMembershipPlanStatus.jusquAu", {
          date: formatInParis(plan.value.season_end_date, { day: "numeric", month: "long", year: "numeric" }, localeIntl, fuseauDe(plan.value)),
        });
      }
      return t("adhesion.useMembershipPlanStatus.saisonEnCours");
    }
    if (plan.value.duration_days) {
      if (plan.value.duration_days === 365) return t("adhesion.useMembershipPlanStatus.unAn");
      if (plan.value.duration_days === 30) return t("adhesion.useMembershipPlanStatus.unMois");
      if (plan.value.duration_days === 90) return t("adhesion.useMembershipPlanStatus.troisMois");
      if (plan.value.duration_days === 180) return t("adhesion.useMembershipPlanStatus.sixMois");
      return t("adhesion.useMembershipPlanStatus.joursPluriel", plan.value.duration_days);
    }
    // Durée fixe SANS nombre de jours = plan sans limite : l'adhésion
    // n'expire jamais (Karim 2026-10-10). Avant : libellé vide, l'adhérent ne
    // savait pas combien de temps il s'engageait.
    return plan.value.duration_type === "fixed" ? t("adhesion.useMembershipPlanStatus.sansLimiteDuree") : "";
  });

  return { isPlanActive, isPlanClosed, isPlanFull, remainingPlaces, isSeasonEnded, durationLabel };
}
